// ── Interface ──────────────────────────────────────────────────────────────────

export interface JournalPhoto {
  key: string;       // R2 object key
  url: string;       // proxy URL: /api/photos/<key>
  caption?: string;
  uploadedAt: string; // ISO datetime string
}

export interface JournalEntry {
  id: string;
  date: string; // ISO date string: YYYY-MM-DD
  type: 'observation' | 'planting' | 'harvest' | 'pest' | 'weather' | 'general';
  title: string;
  content: string;
  plants: string[]; // plant IDs from PLANTS data
  mood: 'great' | 'good' | 'okay' | 'tough' | null;
  photos?: JournalPhoto[];
  createdAt: string; // ISO datetime string
}

// ── Storage ────────────────────────────────────────────────────────────────────

const STORAGE_KEY = 'garden-journal-entries';

function readStorage(): JournalEntry[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as JournalEntry[]) : [];
  } catch {
    return [];
  }
}

function writeStorage(entries: JournalEntry[]): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
  } catch {
    // Storage quota exceeded or unavailable — fail silently
  }
}

function sortEntries(entries: JournalEntry[]): JournalEntry[] {
  return [...entries].sort((a, b) => {
    const dateCmp = b.date.localeCompare(a.date);
    return dateCmp !== 0 ? dateCmp : b.createdAt.localeCompare(a.createdAt);
  });
}

// ── Factory ────────────────────────────────────────────────────────────────────

/** Create a new entry with a generated id and createdAt timestamp */
export function createEntry(
  data: Omit<JournalEntry, 'id' | 'createdAt'>
): JournalEntry {
  return {
    ...data,
    id: `je-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    createdAt: new Date().toISOString(),
  };
}

// ── CRUD ───────────────────────────────────────────────────────────────────────

/** Returns all entries sorted by date (newest first), then createdAt descending */
export function getEntries(): JournalEntry[] {
  return sortEntries(readStorage());
}

/** Returns a single entry by id, or undefined if not found */
export function getEntry(id: string): JournalEntry | undefined {
  return readStorage().find(e => e.id === id);
}

/** Saves a new entry or overwrites an existing one with the same id */
export function saveEntry(entry: JournalEntry): void {
  const all = readStorage();
  const idx = all.findIndex(e => e.id === entry.id);
  if (idx >= 0) {
    all[idx] = entry;
  } else {
    all.push(entry);
  }
  writeStorage(all);
}

/** Removes an entry by id. No-op if id doesn't exist */
export function deleteEntry(id: string): void {
  writeStorage(readStorage().filter(e => e.id !== id));
}

// ── Queries ────────────────────────────────────────────────────────────────────

/** Returns entries that have the given plant id tagged */
export function getEntriesByPlant(plantId: string): JournalEntry[] {
  return sortEntries(readStorage().filter(e => e.plants.includes(plantId)));
}

/** Returns entries of the given type */
export function getEntriesByType(type: JournalEntry['type']): JournalEntry[] {
  return sortEntries(readStorage().filter(e => e.type === type));
}

/** Returns entries whose date falls within [start, end] inclusive (ISO date strings) */
export function getEntriesByDateRange(start: string, end: string): JournalEntry[] {
  return sortEntries(readStorage().filter(e => e.date >= start && e.date <= end));
}
